import {
  Leaf,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react'
import {
  FaYoutube,
  FaTiktok,
  FaFacebook,
  FaInstagram,
  FaXTwitter,
  FaTelegram,
  FaLinkedin,
  FaDiscord,
  FaSeedling,
} from 'react-icons/fa6'
import { Link } from 'react-router'
import { useCatalog } from '../context/CatalogContext'
import { useLanguage } from '../context/LanguageContext'

export default function Footer() {
  const { t } = useLanguage()
  const { shopEnabled } = useCatalog()

  const quickLinks = [
    { href: '/#home', label: t('common.home') },
    { href: '/#project', label: t('nav.project') },
    { href: '/#gites', label: t('gites.kicker') },
    { href: '/#orchards', label: t('orchards.kicker') },
    { href: '/#forest', label: t('forest.kicker') },
    { href: '/#founders', label: t('aboutPage.members') },
  ]

  const pageLinks = [
    ...(shopEnabled ? [{ to: '/boutique', label: t('nav.shop') }] : []),
    { to: '/media', label: t('nav.media') },
    { to: '/partenaires', label: t('nav.partners') },
    { to: '/newsletter', label: t('nav.newsletter') },
    { to: '/contact', label: t('nav.contact') },
  ]

  const socialLinks = [
    { href: 'https://www.youtube.com/@naturegites', label: 'YouTube', Icon: FaYoutube },
    { href: 'https://www.tiktok.com/@nature_gites', label: 'TikTok', Icon: FaTiktok },
    { href: 'https://www.facebook.com/share/12LLKoFyNJ6/', label: 'Facebook', Icon: FaFacebook },
    { href: 'https://www.instagram.com/nature_gites', label: 'Instagram', Icon: FaInstagram },
    { href: 'https://x.com/reve_jean', label: 'X', Icon: FaXTwitter },
    { href: 'https://t.me/+eXLQZxEkmfs0MTU0', label: 'Telegram', Icon: FaTelegram },
    { href: 'https://www.linkedin.com/in/jean-réveillac-373579188', label: 'LinkedIn', Icon: FaLinkedin },
    { href: 'https://discord.gg/gAskaENj', label: 'Discord', Icon: FaDiscord },
    { href: 'https://www.bonzai.pro/nature_gites_Gms', label: 'Bonzai', Icon: FaSeedling },
  ]

  return (
    <footer className="bg-ng-footer text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">

          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-14 w-14 overflow-hidden rounded-[12px] bg-[#f5efe2] p-0.5 shadow-sm ring-1 ring-black/5">
                <img
                  src="/logos/svg/logo.svg"
                  alt={t('brand.name')}
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <p className="text-white/85 leading-relaxed max-w-md mb-6">
              {t('footer.brandText')}
            </p>
            <div className="flex items-center gap-2 text-ng-gold">
              <Leaf className="w-5 h-5" />
              <span className="text-sm font-medium">SCIC N&amp;G</span>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-display text-lg mb-6 font-semibold">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map(({ href, label }) => (
                <li key={href}>
                  <a
                    href={href}
                    className="text-white/85 hover:text-white transition-colors"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Site pages */}
          <div>
            <h3 className="font-display text-lg mb-6 font-semibold">{t('footer.pages')}</h3>
            <ul className="space-y-3">
              {pageLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-white/85 hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-display text-lg mb-6 font-semibold">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li>
                <a href="mailto:natureandgites@gmail.com" className="flex items-center gap-3 text-white/85 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-ng-gold flex-shrink-0" />
                  natureandgites@gmail.com
                </a>
              </li>
              <li>
                <a href="sms:+33624203832" className="flex items-center gap-3 text-white/85 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-ng-mid flex-shrink-0" />
                  06 24 20 38 32
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/85">
                <MapPin className="w-5 h-5 text-ng-brown flex-shrink-0 mt-0.5" />
                <span className="whitespace-pre-line">{t('footer.location')}</span>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-white/50">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">{t('common.legal')}</Link>
              <Link to="/cgv" className="hover:text-white transition-colors">{t('common.cgv')}</Link>
              <Link to="/livraison" className="hover:text-white transition-colors">{t('common.shipping')}</Link>
              <Link to="/retractation" className="hover:text-white transition-colors">{t('common.withdrawal')}</Link>
              <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors">{t('common.privacy')}</Link>
            </div>

            <div className="mt-6">
              <p className="text-sm text-white/60 mb-3">{t('newsletter.community')}</p>
              <div className="flex flex-wrap gap-2">
                {socialLinks.map((s) => {
                  const IconComp = s.Icon
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      title={s.label}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/85 hover:text-white hover:border-white/40 transition-colors"
                    >
                      <IconComp className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">{s.label}</span>
                    </a>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/75">{t('footer.rights')}</p>
            <p className="text-sm text-white/75">{t('footer.president')}</p>
          </div>
        </div>
      </div>
    </footer>
  )
}
